/**
 * Foundation.Decimal (NSDecimal) as the Swift engine computes it on Darwin.
 *
 * A value is sign × mantissa × 10^exponent with an unsigned mantissa below 2^128 and an
 * exponent in [-128, 127]. Every finite value is kept compact (no trailing zeros in the
 * mantissa), which Foundation also does after every operation; the representation matters
 * because operand alignment and division precision depend on it.
 *
 * Arithmetic truncates toward zero wherever digits are lost; it never rounds. The quirks
 * below are reproduced deliberately and pinned by test/fixtures/foundation-vectors.json,
 * recorded from Foundation by tools/foundation-vectors.swift:
 *
 * - Operand alignment (`normalize`) scales the larger-exponent mantissa by 10^diff; if that
 *   overflows it scales by a word-length estimate instead and truncates the other operand,
 *   so `1e36 + 12.345 = 1e36`.
 * - Division computes floor(a·10^38 / b) and then truncates to fit, so `1/3` has 38 digits
 *   while `0.229/12` has 39; it first aligns the operands when `a.exp − b.exp ≥ 19`.
 * - Multiplication wraps an exponent below −128 through Int8 (`1e-120 × 1e-9 = 1e127`);
 *   division returns NaN instead, and also when the 38-digit-scaled quotient is zero.
 */

import { powDouble } from './pow.ts';

export interface FiniteDecimal {
  readonly kind: 'finite';
  readonly negative: boolean;
  readonly mantissa: bigint;
  readonly exponent: number;
}

export interface NaNDecimal {
  readonly kind: 'nan';
}

export type Decimal = FiniteDecimal | NaNDecimal;

const MANTISSA_LIMIT = 1n << 128n;
const MIN_EXPONENT = -128;
const MAX_EXPONENT = 127;
const DIVISION_SCALE = 38;
const TWO_POW_64 = 18446744073709551616;

export const NaN_DECIMAL: NaNDecimal = Object.freeze({ kind: 'nan' });
export const ZERO: FiniteDecimal = Object.freeze({ kind: 'finite', negative: false, mantissa: 0n, exponent: 0 });

const powersOfTen: bigint[] = [1n];
const pow10 = (n: number): bigint => {
  while (powersOfTen.length <= n) powersOfTen.push(powersOfTen[powersOfTen.length - 1]! * 10n);
  return powersOfTen[n]!;
};

function compact(negative: boolean, mantissa: bigint, exponent: number): FiniteDecimal {
  if (mantissa === 0n) return ZERO;
  let m = mantissa;
  let e = exponent;
  while (e < MAX_EXPONENT && m % 10n === 0n) {
    m /= 10n;
    e += 1;
  }
  return { kind: 'finite', negative, mantissa: m, exponent: e };
}

/** Truncate a wide mantissa until it fits in 128 bits. */
function fit(mantissa: bigint, exponent: number): [bigint, number] {
  let m = mantissa;
  let e = exponent;
  while (m >= MANTISSA_LIMIT) {
    m /= 10n;
    e += 1;
  }
  return [m, e];
}

/** Number of 16-bit words Foundation uses for the mantissa (`_length`). */
function wordLength(mantissa: bigint): number {
  return mantissa === 0n ? 0 : Math.ceil(mantissa.toString(16).length / 4);
}

interface Aligned {
  readonly negative: boolean;
  readonly mantissa: bigint;
  readonly exponent: number;
}

/** NSDecimalNormalize: bring both operands to one exponent, truncating when they cannot be. */
function normalize(a: FiniteDecimal, b: FiniteDecimal): [Aligned, Aligned] {
  const diff = a.exponent - b.exponent;
  if (diff === 0) return [a, b];
  const [larger, smaller] = diff > 0 ? [a, b] : [b, a];
  const gap = Math.abs(diff);
  const scaled = larger.mantissa * pow10(gap);
  const align = (l: Aligned, s: Aligned): [Aligned, Aligned] => (diff > 0 ? [l, s] : [s, l]);
  if (scaled < MANTISSA_LIMIT) {
    return align({ ...larger, mantissa: scaled, exponent: smaller.exponent }, smaller);
  }
  const maxPower = Math.floor((8 - wordLength(larger.mantissa)) * 4.81647993);
  const rest = gap - maxPower;
  return align(
    { ...larger, mantissa: larger.mantissa * pow10(maxPower), exponent: larger.exponent - maxPower },
    { ...smaller, mantissa: smaller.mantissa / pow10(rest), exponent: smaller.exponent + rest },
  );
}

/** Division's pre-alignment is abandoned when it would truncate the divisor to zero. */
function alignForDivision(a: FiniteDecimal, b: FiniteDecimal): [Aligned, Aligned] {
  const aligned = normalize(a, b);
  return aligned[1].mantissa === 0n ? [a, b] : aligned;
}

// MARK: Construction

const DECIMAL_TEXT = /^([+-])?(\d*)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/;

/** Parse a decimal string (Decimal(string:locale: en_US_POSIX)). Throws on malformed input. */
export function parseDecimal(text: string): Decimal {
  const match = DECIMAL_TEXT.exec(text.trim());
  if (!match || (match[2] === '' && (match[3] ?? '') === '')) throw new Error(`invalid decimal "${text}"`);
  const [, sign, whole = '', fraction = '', exp = '0'] = match;
  const [m, e] = fit(BigInt(whole + fraction || '0'), Number(exp) - fraction.length);
  return checked(compact(sign === '-', m, e));
}

/** Decimal(Int). */
export function fromInt(value: number): Decimal {
  if (!Number.isSafeInteger(value)) throw new Error(`fromInt expects a safe integer, got ${value}`);
  return compact(value < 0, BigInt(Math.abs(value)), 0);
}

/** Shorthand for literals in engine code: integers or decimal strings, never float literals. */
export const dec = (value: number | string): Decimal => (typeof value === 'number' ? fromInt(value) : parseDecimal(value));

/**
 * Decimal(Double). Scales by 10 in Double arithmetic until the value reaches 2^64, truncates
 * to a UInt64 and compacts: ~17–20 significant digits, e.g. 1/3 → 0.333333333333333248.
 * Swift float literals typed as Decimal (`let r: Decimal = 0.04`) go through this too.
 */
export function fromDouble(value: number): Decimal {
  if (Number.isNaN(value)) return NaN_DECIMAL;
  if (!Number.isFinite(value)) throw new Error('Decimal(Double) of an infinite value traps in Swift');
  if (value === 0) return ZERO;
  let v = Math.abs(value);
  let e = 0;
  while (v < TWO_POW_64) {
    if (e <= MIN_EXPONENT) return NaN_DECIMAL;
    v *= 10;
    e -= 1;
  }
  while (v >= TWO_POW_64) {
    if (e >= MAX_EXPONENT) return NaN_DECIMAL;
    v /= 10;
    e += 1;
  }
  return compact(value < 0, BigInt(Math.trunc(v)), e);
}

/**
 * NSDecimalNumber.doubleValue / Double(truncating:): the mantissa accumulated word by word
 * in Double, then divided (or multiplied) by 10 once per exponent step. Not correctly
 * rounded: 2/3 → 0.6666666666666667.
 */
export function toDouble(value: Decimal): number {
  if (value.kind === 'nan') return Number.NaN;
  let d = 0;
  const hex = value.mantissa.toString(16);
  const firstWord = hex.length % 4 || 4;
  for (let i = 0; i < hex.length; i += i === 0 ? firstWord : 4) {
    d = d * 65536 + Number.parseInt(hex.slice(i, i === 0 ? firstWord : i + 4), 16);
  }
  if (value.exponent < 0) for (let i = value.exponent; i < 0; i++) d /= 10;
  else for (let i = 0; i < value.exponent; i++) d *= 10;
  return value.negative ? -d : d;
}

function checked(value: FiniteDecimal): Decimal {
  return value.exponent < MIN_EXPONENT || value.exponent > MAX_EXPONENT ? NaN_DECIMAL : value;
}

// MARK: Arithmetic

export function add(a: Decimal, b: Decimal): Decimal {
  if (a.kind === 'nan' || b.kind === 'nan') return NaN_DECIMAL;
  if (a.mantissa === 0n) return b;
  if (b.mantissa === 0n) return a;
  const [x, y] = normalize(a, b);
  if (x.negative === y.negative) {
    const [m, e] = fit(x.mantissa + y.mantissa, x.exponent);
    return checked(compact(x.negative, m, e));
  }
  if (x.mantissa === y.mantissa) return ZERO;
  const [big, small] = x.mantissa > y.mantissa ? [x, y] : [y, x];
  return compact(big.negative, big.mantissa - small.mantissa, x.exponent);
}

export const negate = (a: Decimal): Decimal =>
  a.kind === 'nan' || a.mantissa === 0n ? a : { ...a, negative: !a.negative };

export const sub = (a: Decimal, b: Decimal): Decimal => add(a, negate(b));

export function mul(a: Decimal, b: Decimal): Decimal {
  if (a.kind === 'nan' || b.kind === 'nan') return NaN_DECIMAL;
  if (a.mantissa === 0n || b.mantissa === 0n) return ZERO;
  const [m, e] = fit(a.mantissa * b.mantissa, a.exponent + b.exponent);
  if (e > MAX_EXPONENT) return NaN_DECIMAL;
  return compact(a.negative !== b.negative, m, e < MIN_EXPONENT ? e + 256 : e);
}

export function div(a: Decimal, b: Decimal): Decimal {
  if (a.kind === 'nan' || b.kind === 'nan' || b.mantissa === 0n) return NaN_DECIMAL;
  if (a.mantissa === 0n) return ZERO;
  const [x, y] = a.exponent - b.exponent >= 19 ? alignForDivision(a, b) : [a, b];
  const [m, e] = fit((x.mantissa * pow10(DIVISION_SCALE)) / y.mantissa, x.exponent - y.exponent - DIVISION_SCALE);
  if (m === 0n || e < MIN_EXPONENT || e > MAX_EXPONENT) return NaN_DECIMAL;
  return compact(a.negative !== b.negative, m, e);
}

export const abs = (a: Decimal): Decimal => (a.kind === 'finite' && a.negative ? negate(a) : a);

export const sum = (values: Iterable<Decimal>): Decimal => {
  let total: Decimal = ZERO;
  for (const value of values) total = add(total, value);
  return total;
};

/** Pow via Double, as every `pow(Decimal, Decimal)` in the Swift engine does. */
export const pow = (base: Decimal, exponent: Decimal): Decimal =>
  fromDouble(powDouble(toDouble(base), toDouble(exponent)));

/** NSDecimalRound(.plain): round half away from zero to `scale` fractional digits. */
export function roundPlain(value: Decimal, scale: number): Decimal {
  if (value.kind === 'nan' || value.exponent >= -scale) return value;
  const drop = -scale - value.exponent;
  const divisor = pow10(drop);
  const quotient = value.mantissa / divisor;
  const remainder = value.mantissa % divisor;
  const rounded = remainder * 2n >= divisor ? quotient + 1n : quotient;
  return compact(value.negative, rounded, -scale);
}

/** The `NSDecimalNumberHandler(roundingMode: .plain, scale: 2)` used throughout the engine. */
export const round2 = (value: Decimal): Decimal => roundPlain(value, 2);

// MARK: Comparison

/** NSDecimalCompare, including its truncating alignment. NaN orders before everything. */
export function compare(a: Decimal, b: Decimal): -1 | 0 | 1 {
  if (a.kind === 'nan') return b.kind === 'nan' ? 0 : -1;
  if (b.kind === 'nan') return 1;
  if (a.negative !== b.negative) return a.negative ? -1 : 1;
  if (a.mantissa === 0n || b.mantissa === 0n) {
    if (a.mantissa === b.mantissa) return 0;
    return a.mantissa === 0n ? -1 : 1;
  }
  const [x, y] = normalize(a, b);
  const magnitude = x.mantissa === y.mantissa ? 0 : x.mantissa > y.mantissa ? 1 : -1;
  return a.negative ? (-magnitude as -1 | 0 | 1) : magnitude;
}

export const eq = (a: Decimal, b: Decimal): boolean => compare(a, b) === 0;
export const lt = (a: Decimal, b: Decimal): boolean => compare(a, b) < 0;
export const lte = (a: Decimal, b: Decimal): boolean => compare(a, b) <= 0;
export const gt = (a: Decimal, b: Decimal): boolean => compare(a, b) > 0;
export const gte = (a: Decimal, b: Decimal): boolean => compare(a, b) >= 0;
export const min = (a: Decimal, b: Decimal): Decimal => (lte(a, b) ? a : b);
export const max = (a: Decimal, b: Decimal): Decimal => (gte(a, b) ? a : b);
export const isZero = (a: Decimal): boolean => a.kind === 'finite' && a.mantissa === 0n;
export const isNaNDecimal = (a: Decimal): a is NaNDecimal => a.kind === 'nan';

// MARK: Formatting

/** Decimal.description: plain notation, no trailing zeros, "NaN" for NaN. */
export function formatDecimal(value: Decimal): string {
  if (value.kind === 'nan') return 'NaN';
  if (value.mantissa === 0n) return '0';
  const digits = value.mantissa.toString();
  const sign = value.negative ? '-' : '';
  if (value.exponent >= 0) return sign + digits + '0'.repeat(value.exponent);
  const point = digits.length + value.exponent;
  if (point > 0) return `${sign}${digits.slice(0, point)}.${digits.slice(point)}`;
  return `${sign}0.${'0'.repeat(-point)}${digits}`;
}
